import React, {createContext, useContext, useMemo} from 'react';

import {useHeaderCollapse} from 'ui-library/headers/Header';

interface ProcedureRunOverviewScrollContextValue {
  scrollRef: React.RefCallback<HTMLDivElement>;
  isHeaderCollapsed: boolean;
}

const ProcedureRunOverviewScrollContext = createContext<ProcedureRunOverviewScrollContextValue | null>(null);

/**
 * Returns the run overview scroll state, or null when rendered outside the provider (the legacy
 * shell). Callers use the null case to opt out of header collapse while still content-scrolling.
 */
export function useProcedureRunOverviewScroll(): ProcedureRunOverviewScrollContextValue | null {
  return useContext(ProcedureRunOverviewScrollContext);
}

export function ProcedureRunOverviewScrollProvider({children}: {children: React.ReactNode}): JSX.Element {
  // Reuse the shared header-collapse state machine. Its callback ref attaches the scroll listener
  // once the async overview content mounts and resets the collapse when that content detaches (e.g.
  // switching to the design tab), so the still-mounted header does not keep a stale collapsed state.
  const {scrollRef, isCollapsed} = useHeaderCollapse();

  const value = useMemo(() => ({scrollRef, isHeaderCollapsed: isCollapsed}), [scrollRef, isCollapsed]);

  return (
    <ProcedureRunOverviewScrollContext.Provider value={value}>
      {children}
    </ProcedureRunOverviewScrollContext.Provider>
  );
}
